import { faPlus, faTimes, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MenuItem } from '@mui/material';
import { DecimalInput } from 'components/Form/DecimalInput/DecimalInput';
import ExpandableSelector from 'components/Form/ExpandableSelector/ExpandableSelector';
import { Input } from 'components/Form/Input/Input';
import { InputGroup } from 'components/Form/InputGroup/InputGroup';
import { InputGroupDivider } from 'components/Form/InputGroup/InputGroupDivider';
import { InputGroupSelect } from 'components/Form/InputGroup/InputGroupSelect';
import { Label } from 'components/Form/Label/Label';
import { RadioGroup } from 'components/Form/RadioGroup/RadioGroup';
import { Select } from 'components/Form/Select/Select';
import { Button } from 'components/UI/Button/Button';
import { Card } from 'components/UI/Card/Card';
import { CardContent } from 'components/UI/Card/Content/CardContent';
import { Switch } from 'components/UI/Switch/Switch';
import { useMasterData } from 'hooks/useMasterData';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { Control, Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { generateTimeOptions } from 'utils/timeUtils';
import { Medication, MedicationSchedule, MedicationScheduleMode } from 'types/OnlineBooking';
import { getMedicationMaxDecimals } from 'utils/dosageDecimalsConfig';
import styles from './MedicationForm.module.scss';

interface MedicationFormProps {
    value?: Array<Medication>;
    onChange?: (medications: Array<Medication>) => void;
    bookingStartDate?: Moment;
    bookingEndDate?: Moment;
    isDaycare?: boolean;
    timeZone?: string;
}

interface MedicationFormData {
    medications: Array<Medication>;
}

interface MedicationScheduleState {
    mode: MedicationScheduleMode;
    dailyTimes: string[];
    specificDays: Array<{
        date: string;
        enabled: boolean;
        times: string[];
    }>;
}

interface DosageDecimalInputProps {
    control: Control<MedicationFormData>;
    medIndex: number;
    units: Array<{ id: number; name: string }>;
    value: number | string | undefined;
    onChange: (v: number) => void;
}

// Watches the selected unit and forwards a scale matching CSW's per-unit max decimals.
const DosageDecimalInput: React.FC<DosageDecimalInputProps> = ({
    control,
    medIndex,
    units,
    value,
    onChange
}) => {
    const unitId = useWatch({ control, name: `medications.${medIndex}.dosage.unit` as const });
    const unitName = units.find(u => u.id === unitId)?.name;
    const scale = getMedicationMaxDecimals(unitName);

    return (
        <DecimalInput
            value={value}
            onChange={onChange}
            scale={scale}
            placeholder={scale > 0 ? `Ex. 0.${'5'.padEnd(scale, '0').slice(0, scale)}` : 'Ex. 1'}
        />
    );
};

// Helper functions to convert between time strings and MedicationSchedule objects
const timeStringToSchedule = (timeString: string, date?: moment.Moment): MedicationSchedule => {
    const [hour, minute] = timeString.split(':').map(Number);
    return {
        hour,
        minute,
        date
    };
};

const scheduleToTimeString = (schedule: MedicationSchedule): string => {
    return `${schedule.hour.toString().padStart(2, '0')}:${schedule.minute
        .toString()
        .padStart(2, '0')}`;
};

// Helper function to get next time with 15 minute increment
const getNextTime = (currentTime: string): string => {
    const [hour, minute] = currentTime.split(':').map(Number);
    let newMinute = minute + 15;
    let newHour = hour;

    if (newMinute >= 60) {
        newMinute = newMinute - 60;
        newHour = hour + 1;

        if (newHour >= 24) {
            newHour = 0;
        }
    }

    return `${newHour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}`;
};

export const MedicationForm: React.FC<MedicationFormProps> = ({
    value = [],
    onChange,
    bookingStartDate,
    bookingEndDate,
    isDaycare = false,
    timeZone
}) => {
    const { masterData } = useMasterData();
    const [isMedicationEnabled, setIsMedicationEnabled] = React.useState(value.length > 0);
    const timeOptions = React.useMemo(() => generateTimeOptions(), []);
    const onChangeRef = React.useRef(onChange);
    const hasAutoAddedRef = React.useRef(false);

    // Filter time options for daycare based on booking start/end times
    const filteredTimeOptions = React.useMemo(() => {
        if (!isDaycare || !bookingStartDate || !bookingEndDate) {
            return timeOptions;
        }

        return timeOptions.filter(option => {
            const [hour, minute] = option.id.split(':').map(Number);
            const optionMinutes = hour * 60 + minute;
            const startMinutes = bookingStartDate.hour() * 60 + bookingStartDate.minute();
            const endMinutes = bookingEndDate.hour() * 60 + bookingEndDate.minute();

            return optionMinutes >= startMinutes && optionMinutes <= endMinutes;
        });
    }, [isDaycare, bookingStartDate, bookingEndDate, timeOptions]);

    // Keep ref updated
    React.useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // Track schedule mode and times for each medication separately
    const [medicationSchedules, setMedicationSchedules] = React.useState<
        Record<number, MedicationScheduleState>
    >({});

    const defaultMedication: Medication = React.useMemo(
        () => ({
            name: '',
            methodType: 0,
            dosage: {
                quantity: NaN,
                unit: masterData.medicationDosageUnits[0]?.id || 0
            },
            schedule: [],
            scheduleMode: MedicationScheduleMode.Daily
        }),
        [masterData.medicationDosageUnits]
    );

    // Ensure all medications have proper default values
    const normalizeMedication = (medication: Medication): Medication => ({
        name: medication.name || '',
        methodType: medication.methodType ?? masterData.medicationMethodTypes[0]?.id ?? 0,
        dosage: {
            quantity: medication.dosage?.quantity ?? 0,
            unit: medication.dosage?.unit ?? masterData.medicationDosageUnits[0]?.id ?? 0
        },
        instructions: medication.instructions,
        schedule: medication.schedule || [],
        scheduleMode: medication.scheduleMode || MedicationScheduleMode.Daily
    });

    const initialMedications = value.length > 0 ? value.map(normalizeMedication) : [];

    const { control, watch, setValue } = useForm<MedicationFormData>({
        defaultValues: {
            medications: initialMedications
        },
        mode: 'onChange'
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'medications'
    });

    const generateSpecificDays = React.useCallback(() => {
        if (!bookingStartDate || !bookingEndDate) return [];

        const days: Array<{ date: string; enabled: boolean; times: string[] }> = [];
        const current = bookingStartDate.clone();

        while (current.isSameOrBefore(bookingEndDate, 'day')) {
            days.push({
                date: current.format('YYYY-MM-DD'),
                enabled: false,
                times: []
            });
            current.add(1, 'day');
        }

        return days;
    }, [bookingStartDate, bookingEndDate]);

    // Initialize medicationSchedules from existing medications
    React.useEffect(() => {
        if (value.length > 0 && Object.keys(medicationSchedules).length === 0) {
            const initialSchedules: Record<number, MedicationScheduleState> = {};

            value.forEach((med, index) => {
                const dailySchedules: string[] = [];
                const specificDaysMap: Record<string, string[]> = {};

                // Convert MedicationSchedule[] back to internal state format
                med.schedule.forEach(schedule => {
                    const timeString = scheduleToTimeString(schedule);

                    if (schedule.date) {
                        // Specific day
                        const dateKey = schedule.date.format('YYYY-MM-DD');
                        if (!specificDaysMap[dateKey]) {
                            specificDaysMap[dateKey] = [];
                        }
                        specificDaysMap[dateKey].push(timeString);
                    } else {
                        // Daily
                        dailySchedules.push(timeString);
                    }
                });

                // Build specificDays array
                const specificDays = generateSpecificDays().map(day => ({
                    ...day,
                    enabled: !!specificDaysMap[day.date],
                    times: specificDaysMap[day.date] || []
                }));

                initialSchedules[index] = {
                    mode: med.scheduleMode || MedicationScheduleMode.Daily,
                    dailyTimes: dailySchedules.length > 0 ? dailySchedules : ['08:00'],
                    specificDays
                };
            });

            setMedicationSchedules(initialSchedules);
        }
    }, [value, medicationSchedules, generateSpecificDays]);

    // Watch all form values and call onChange when they change
    React.useEffect(() => {
        const subscription = watch(data => {
            if (onChangeRef.current) {
                onChangeRef.current(
                    isMedicationEnabled ? (data.medications as Array<Medication>) : []
                );
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, isMedicationEnabled]);

    // Notify parent when medication is disabled
    React.useEffect(() => {
        if (!isMedicationEnabled && onChangeRef.current) {
            onChangeRef.current([]);
        }
    }, [isMedicationEnabled]);

    // Auto-add medication item when enabled
    React.useEffect(() => {
        if (isMedicationEnabled && fields.length === 0 && !hasAutoAddedRef.current) {
            hasAutoAddedRef.current = true;
            const newIndex = 0;

            // For daycare, use the first available filtered time option
            const defaultTimeString =
                isDaycare && filteredTimeOptions.length > 0 ? filteredTimeOptions[0].id : '08:00';

            const scheduleState = {
                mode: MedicationScheduleMode.Daily,
                dailyTimes: [defaultTimeString],
                specificDays: generateSpecificDays()
            };

            // Append medication with schedule already set
            const medicationWithSchedule: Medication = {
                ...defaultMedication,
                schedule: [timeStringToSchedule(defaultTimeString)],
                scheduleMode: MedicationScheduleMode.Daily
            };

            append(medicationWithSchedule);
            setMedicationSchedules(prev => ({
                ...prev,
                [newIndex]: scheduleState
            }));
        }

        // Reset the ref when medication is disabled
        if (!isMedicationEnabled) {
            hasAutoAddedRef.current = false;
        }
    }, [
        isMedicationEnabled,
        fields.length,
        append,
        defaultMedication,
        generateSpecificDays,
        isDaycare,
        filteredTimeOptions
    ]);

    const handleAddMedication = () => {
        const newIndex = fields.length;

        // For daycare, use the first available filtered time option
        let defaultTimeString = '08:00';
        if (isDaycare && filteredTimeOptions.length > 0) {
            defaultTimeString = filteredTimeOptions[0].id;
        } else {
            // Find the latest time across all existing medications (only daily schedules)
            let latestTimeString = '07:45'; // Default so first medication will be 08:00

            fields.forEach(field => {
                const med = field as Medication;
                if (med.schedule && med.schedule.length > 0) {
                    med.schedule.forEach(schedule => {
                        if (!schedule.date) {
                            // Only consider daily schedules
                            const timeString = scheduleToTimeString(schedule);
                            const [hour, minute] = timeString.split(':').map(Number);
                            const [latestHour, latestMinute] = latestTimeString
                                .split(':')
                                .map(Number);

                            // Compare times
                            if (
                                hour > latestHour ||
                                (hour === latestHour && minute > latestMinute)
                            ) {
                                latestTimeString = timeString;
                            }
                        }
                    });
                }
            });

            // Increment by 15 minutes
            defaultTimeString = getNextTime(latestTimeString);
        }

        const scheduleState = {
            mode: MedicationScheduleMode.Daily,
            dailyTimes: [defaultTimeString],
            specificDays: generateSpecificDays()
        };

        // Append medication with schedule already set
        const medicationWithSchedule: Medication = {
            ...defaultMedication,
            schedule: [timeStringToSchedule(defaultTimeString)],
            scheduleMode: MedicationScheduleMode.Daily
        };

        append(medicationWithSchedule);
        setMedicationSchedules(prev => ({
            ...prev,
            [newIndex]: scheduleState
        }));
    };

    const handleRemoveMedication = (index: number) => {
        remove(index);
        setMedicationSchedules(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });

        // Auto-disable switch when last medication is deleted
        if (fields.length === 1) {
            setIsMedicationEnabled(false);
        }
    };

    const getScheduleState = (index: number): MedicationScheduleState => {
        const state = medicationSchedules[index] || {
            mode: MedicationScheduleMode.Daily,
            dailyTimes: ['08:00'],
            specificDays: generateSpecificDays()
        };

        // Ensure daily mode always has at least one dose
        if (state.mode === MedicationScheduleMode.Daily && state.dailyTimes.length === 0) {
            state.dailyTimes = ['08:00'];
        }

        return state;
    };

    // Get available time options for a specific dose, excluding already selected times
    const getAvailableTimeOptions = (
        medIndex: number,
        currentTime: string,
        selectedTimes: string[]
    ) => {
        return filteredTimeOptions.filter(
            option => option.id === currentTime || !selectedTimes.includes(option.id)
        );
    };

    const updateScheduleState = (index: number, updates: Partial<MedicationScheduleState>) => {
        setMedicationSchedules(prev => ({
            ...prev,
            [index]: {
                ...getScheduleState(index),
                ...updates
            }
        }));
    };

    // Sync schedule state to form field
    const syncScheduleToForm = (medIndex: number, scheduleState: MedicationScheduleState) => {
        const schedule: MedicationSchedule[] = [];

        if (scheduleState.mode === MedicationScheduleMode.Daily) {
            // Daily mode: date is undefined
            scheduleState.dailyTimes.forEach(timeString => {
                schedule.push(timeStringToSchedule(timeString));
            });
        } else {
            // Specific days mode: date is set for each enabled day
            scheduleState.specificDays.forEach(day => {
                if (day.enabled) {
                    // Use moment.tz to create the date in the business timezone
                    // This ensures the date is interpreted correctly regardless of browser timezone
                    const date = timeZone ? moment.tz(day.date, timeZone) : moment(day.date);
                    day.times.forEach(timeString => {
                        schedule.push(timeStringToSchedule(timeString, date));
                    });
                }
            });
        }

        // Update the form fields using setValue
        setValue(`medications.${medIndex}.schedule`, schedule, { shouldValidate: true });
        setValue(`medications.${medIndex}.scheduleMode`, scheduleState.mode, {
            shouldValidate: true
        });
    };

    const addDoseTime = (medIndex: number) => {
        const scheduleState = getScheduleState(medIndex);
        if (scheduleState.mode === MedicationScheduleMode.Daily) {
            // Find the latest time in current doses
            let latestTime = '07:45'; // Default so first will be 08:00
            if (scheduleState.dailyTimes.length > 0) {
                latestTime = scheduleState.dailyTimes[scheduleState.dailyTimes.length - 1];
            }

            // Calculate next time (increment by 15 minutes)
            const nextTime = getNextTime(latestTime);

            const newState = {
                dailyTimes: [...scheduleState.dailyTimes, nextTime]
            };
            updateScheduleState(medIndex, newState);
            syncScheduleToForm(medIndex, { ...scheduleState, ...newState });
        }
    };

    const removeDoseTime = (medIndex: number, timeIndex: number) => {
        const scheduleState = getScheduleState(medIndex);
        if (scheduleState.mode === MedicationScheduleMode.Daily) {
            const newState = {
                dailyTimes: scheduleState.dailyTimes.filter((_, i) => i !== timeIndex)
            };
            updateScheduleState(medIndex, newState);
            syncScheduleToForm(medIndex, { ...scheduleState, ...newState });
        }
    };

    const addSpecificDayTime = (medIndex: number, dayIndex: number) => {
        const scheduleState = getScheduleState(medIndex);
        const updatedDays = [...scheduleState.specificDays];

        // Find the latest time in this specific day's doses
        let latestTime = '07:45'; // Default so first will be 08:00
        if (updatedDays[dayIndex].times.length > 0) {
            latestTime = updatedDays[dayIndex].times[updatedDays[dayIndex].times.length - 1];
        }

        // Calculate next time (increment by 15 minutes)
        const nextTime = getNextTime(latestTime);

        updatedDays[dayIndex] = {
            ...updatedDays[dayIndex],
            times: [...updatedDays[dayIndex].times, nextTime]
        };
        const newState = { specificDays: updatedDays };
        updateScheduleState(medIndex, newState);
        syncScheduleToForm(medIndex, { ...scheduleState, ...newState });
    };

    const removeSpecificDayTime = (medIndex: number, dayIndex: number, timeIndex: number) => {
        const scheduleState = getScheduleState(medIndex);
        const updatedDays = [...scheduleState.specificDays];
        updatedDays[dayIndex] = {
            ...updatedDays[dayIndex],
            times: updatedDays[dayIndex].times.filter((_, i) => i !== timeIndex)
        };
        const newState = { specificDays: updatedDays };
        updateScheduleState(medIndex, newState);
        syncScheduleToForm(medIndex, { ...scheduleState, ...newState });
    };

    const toggleSpecificDay = (medIndex: number, dayIndex: number) => {
        const scheduleState = getScheduleState(medIndex);
        const updatedDays = [...scheduleState.specificDays];
        const isEnabling = !updatedDays[dayIndex].enabled;

        updatedDays[dayIndex] = {
            ...updatedDays[dayIndex],
            enabled: isEnabling,
            // Auto-add 08:00 dose when enabling a day
            times:
                isEnabling && updatedDays[dayIndex].times.length === 0
                    ? ['08:00']
                    : updatedDays[dayIndex].times
        };
        const newState = { specificDays: updatedDays };
        updateScheduleState(medIndex, newState);
        syncScheduleToForm(medIndex, { ...scheduleState, ...newState });
    };

    if (!isMedicationEnabled) {
        return (
            <div className="flex flex-col gap-4 items-start">
                <h4 className="h4 w-full flex items-center gap-2">
                    <Switch checked={false} onCheckedChange={setIsMedicationEnabled} />
                    Medication
                </h4>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 items-start">
            <div className="flex flex-col gap-6 w-full">
                {fields.map((field, index) => {
                    const scheduleState = getScheduleState(index);

                    return (
                        <div key={field.id} className="w-full flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                                <h4 className="h4 flex items-center gap-2">
                                    {index === 0 && (
                                        <Switch
                                            checked={true}
                                            onCheckedChange={setIsMedicationEnabled}
                                        />
                                    )}
                                    Medication {index + 1}
                                </h4>

                                {index > 0 && (
                                    <Button
                                        variant="link"
                                        onClick={() => handleRemoveMedication(index)}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                    </Button>
                                )}
                            </div>

                            <Card>
                                <CardContent className={styles.cardContent}>
                                    <div className="flex flex-col">
                                        <Label>Medication Name</Label>
                                        <Controller
                                            name={`medications.${index}.name`}
                                            control={control}
                                            rules={{ required: 'Medication name is required' }}
                                            render={({ field: { value, onChange } }) => (
                                                <Input
                                                    placeholder="Medication Name"
                                                    className="mt-0"
                                                    value={value}
                                                    onChange={e => onChange(e.target.value)}
                                                />
                                            )}
                                        />
                                    </div>

                                    <div className={styles.methodDosageRow}>
                                        <div className="flex flex-col flex-1">
                                            <Label>Method</Label>
                                            <Controller
                                                name={`medications.${index}.methodType`}
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <Select
                                                        placeholder="Select Food Type"
                                                        options={masterData.medicationMethodTypes.map(
                                                            type => ({
                                                                id: type.id.toString(),
                                                                name: type.name
                                                            })
                                                        )}
                                                        value={value?.toString() || ''}
                                                        onChange={val =>
                                                            onChange(
                                                                val ? Number(val.id) : undefined
                                                            )
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className="flex flex-col">
                                            <Label>Dosage</Label>
                                            <InputGroup className={styles.foodQtyInputGroup}>
                                                <Controller
                                                    name={`medications.${index}.dosage.quantity`}
                                                    control={control}
                                                    rules={{
                                                        required: 'Dosage is required',
                                                        min: {
                                                            value: 0.001,
                                                            message: 'Minimum 0.001'
                                                        }
                                                    }}
                                                    render={({ field: { value, onChange } }) => (
                                                        <DosageDecimalInput
                                                            control={control}
                                                            medIndex={index}
                                                            units={masterData.medicationDosageUnits}
                                                            value={value}
                                                            onChange={onChange}
                                                        />
                                                    )}
                                                />
                                                <InputGroupDivider />
                                                <Controller
                                                    name={`medications.${index}.dosage.unit`}
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => (
                                                        <InputGroupSelect
                                                            value={value?.toString() || ''}
                                                            onChange={e =>
                                                                onChange(Number(e.target.value))
                                                            }
                                                        >
                                                            {masterData.medicationDosageUnits.map(
                                                                unit => (
                                                                    <MenuItem
                                                                        key={unit.id}
                                                                        value={unit.id.toString()}
                                                                    >
                                                                        {unit.name}
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </InputGroupSelect>
                                                    )}
                                                />
                                            </InputGroup>
                                        </div>
                                    </div>

                                    <div className="flex flex-col">
                                        <Label>Medication Instructions</Label>
                                        <Controller
                                            name={`medications.${index}.instructions`}
                                            control={control}
                                            render={({ field: { value, onChange } }) => (
                                                <Input
                                                    placeholder="Ex. Take with meal"
                                                    className="mt-0"
                                                    value={value || ''}
                                                    onChange={e => onChange(e.target.value)}
                                                />
                                            )}
                                        />
                                    </div>

                                    {!isDaycare && (
                                        <div className={styles.medicalSchedule}>
                                            <Label className="mb-0">Medical Schedule</Label>

                                            <RadioGroup
                                                className={styles.radioGroup}
                                                options={[
                                                    {
                                                        id: MedicationScheduleMode.Daily,
                                                        name: 'Daily'
                                                    },
                                                    {
                                                        id: MedicationScheduleMode.SpecificDays,
                                                        name: 'Specific Days'
                                                    }
                                                ]}
                                                value={scheduleState.mode}
                                                onChange={val => {
                                                    const newState = {
                                                        mode: val.id as MedicationScheduleMode
                                                    };
                                                    updateScheduleState(index, newState);
                                                    syncScheduleToForm(index, {
                                                        ...scheduleState,
                                                        ...newState
                                                    });
                                                }}
                                            />
                                        </div>
                                    )}

                                    {scheduleState.mode === MedicationScheduleMode.Daily ? (
                                        <>
                                            <div className="flex flex-col gap-2">
                                                {scheduleState.dailyTimes.map((time, timeIndex) => (
                                                    <div
                                                        key={timeIndex}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <p className={styles.doseLabel}>
                                                            Dose {timeIndex + 1}
                                                        </p>

                                                        <ExpandableSelector
                                                            label="Time"
                                                            options={getAvailableTimeOptions(
                                                                index,
                                                                time,
                                                                scheduleState.dailyTimes
                                                            )}
                                                            value={time}
                                                            onChange={newTime => {
                                                                const updatedTimes = [
                                                                    ...scheduleState.dailyTimes
                                                                ];
                                                                updatedTimes[timeIndex] =
                                                                    newTime.id;
                                                                const newState = {
                                                                    dailyTimes: updatedTimes
                                                                };
                                                                updateScheduleState(
                                                                    index,
                                                                    newState
                                                                );
                                                                syncScheduleToForm(index, {
                                                                    ...scheduleState,
                                                                    ...newState
                                                                });
                                                            }}
                                                        />

                                                        {timeIndex > 0 && (
                                                            <Button
                                                                variant="link"
                                                                onClick={() =>
                                                                    removeDoseTime(index, timeIndex)
                                                                }
                                                                type="button"
                                                            >
                                                                <FontAwesomeIcon icon={faTimes} />
                                                            </Button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            <div className={styles.addDoseSchedule}>
                                                <button
                                                    type="button"
                                                    onClick={() => addDoseTime(index)}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} />
                                                    <span>Add dose schedule</span>
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex flex-col gap-3">
                                            {scheduleState.specificDays.map((day, dayIndex) => (
                                                <div
                                                    key={day.date}
                                                    className={styles.specificDayContainer}
                                                >
                                                    <button
                                                        type="button"
                                                        className={styles.specificDay}
                                                        onClick={() =>
                                                            toggleSpecificDay(index, dayIndex)
                                                        }
                                                    >
                                                        <Label className="mb-0">
                                                            {moment(day.date).format(
                                                                'MMM DD, YYYY'
                                                            )}
                                                        </Label>
                                                        <Switch
                                                            checked={day.enabled}
                                                            onCheckedChange={() => {
                                                                // Prevent double toggle - button already handles it
                                                            }}
                                                        />
                                                    </button>

                                                    {day.enabled && (
                                                        <>
                                                            <div className="flex flex-col gap-2">
                                                                {day.times.map(
                                                                    (time, timeIndex) => (
                                                                        <div
                                                                            key={timeIndex}
                                                                            className="flex items-center gap-4"
                                                                        >
                                                                            <p
                                                                                className={
                                                                                    styles.doseLabel
                                                                                }
                                                                            >
                                                                                Dose {timeIndex + 1}
                                                                            </p>
                                                                            <ExpandableSelector
                                                                                label="Time"
                                                                                options={getAvailableTimeOptions(
                                                                                    index,
                                                                                    time,
                                                                                    day.times
                                                                                )}
                                                                                value={time}
                                                                                onChange={newTime => {
                                                                                    const updatedDays =
                                                                                        [
                                                                                            ...scheduleState.specificDays
                                                                                        ];
                                                                                    updatedDays[
                                                                                        dayIndex
                                                                                    ].times[
                                                                                        timeIndex
                                                                                    ] = newTime.id;
                                                                                    const newState =
                                                                                        {
                                                                                            specificDays:
                                                                                                updatedDays
                                                                                        };
                                                                                    updateScheduleState(
                                                                                        index,
                                                                                        newState
                                                                                    );
                                                                                    syncScheduleToForm(
                                                                                        index,
                                                                                        {
                                                                                            ...scheduleState,
                                                                                            ...newState
                                                                                        }
                                                                                    );
                                                                                }}
                                                                            />

                                                                            {timeIndex > 0 && (
                                                                                <Button
                                                                                    variant="link"
                                                                                    onClick={() =>
                                                                                        removeSpecificDayTime(
                                                                                            index,
                                                                                            dayIndex,
                                                                                            timeIndex
                                                                                        )
                                                                                    }
                                                                                    type="button"
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        icon={
                                                                                            faTimes
                                                                                        }
                                                                                    />
                                                                                </Button>
                                                                            )}
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>

                                                            <div className={styles.addDoseSchedule}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        addSpecificDayTime(
                                                                            index,
                                                                            dayIndex
                                                                        )
                                                                    }
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faPlus}
                                                                    />
                                                                    <span>Add dose</span>
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    );
                })}
            </div>

            {isMedicationEnabled && (
                <Button variant="link" onClick={handleAddMedication} type="button">
                    <FontAwesomeIcon icon={faPlus} />
                    <span className="underline">Add medication</span>
                </Button>
            )}
        </div>
    );
};
